import { motion, useTransform } from 'motion/react';
import { ArrowDown } from 'lucide-react';
import { ExternalArrow } from './DirIcon';
import { useInViewOnceNear, useWindowScrollY } from '../hooks';
import { Link } from 'react-router-dom';
import { scrollToId } from '../lib/scroll';
import { useI18n } from '../i18n';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

export function Hero() {
  const { t, locale } = useI18n();
  const lp = useLocalizedPath();
  const isAr = locale === 'ar';
  const scrollY = useWindowScrollY();
  const drift = useTransform(scrollY, [0, 800], [0, -140]);
  const fade = useTransform(scrollY, [0, 560], [1, 0]);
  const { ref: marqueeRef, inView: marqueeOn } = useInViewOnceNear<HTMLDivElement>();
  const marquee = t.hero.marquee;

  const goto = (id: string) => scrollToId(id);

  return (
    <section id="home" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
      <motion.div
        style={{ y: drift, opacity: fade }}
        className="px-6 md:px-12 w-full flex-1 flex flex-col justify-center pt-20 md:pt-24"
      >
        <span className="mono-label chip mb-8 md:mb-12" style={{ color: 'var(--accent)' }}>
          {t.hero.kicker}
        </span>

        <h1
          className={`display hero-display${isAr ? ' is-ar' : ''}`}
          style={{
            fontSize: isAr ? 'var(--display-hero-ar)' : 'var(--display-hero)',
            lineHeight: isAr ? 1.2 : undefined,
          }}
        >
          <span>{t.hero.line1}</span>
          <span
            className="stroke-text"
            style={isAr ? undefined : { marginInlineStart: '0.35em' }}
          >
            {t.hero.line2}
          </span>
          {t.hero.line3 ? (
            <span>
              {isAr ? null : ' '}
              {t.hero.line3}
              <span style={{ color: 'var(--accent)' }}>.</span>
            </span>
          ) : (
            <span style={{ color: 'var(--accent)' }}>.</span>
          )}
        </h1>

        <div className="mt-10 md:mt-14" style={{ maxWidth: isAr ? 520 : 480 }}>
          <p
            style={{
              color: 'var(--muted)',
              lineHeight: isAr ? 1.85 : 1.7,
              fontSize: '1.05rem',
            }}
          >
            {t.hero.body}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-4">
            <button
              onClick={() => goto('work')}
              className="link-line"
              style={{ color: 'var(--ink)', fontWeight: 500 }}
            >
              {t.hero.seeWork}
            </button>
            <Link
              to={lp('/devis')}
              data-cursor
              className="btn-pill inline-flex items-center gap-2"
              style={{ textAlign: 'start', lineHeight: 1.35 }}
            >
              {t.hero.start.replace(/\n/g, ' ')} <ExternalArrow size={14} />
            </Link>
          </div>
        </div>
      </motion.div>

      <div className="w-full">
        <div className="px-6 md:px-12 pb-4 flex items-center justify-between">
          <span className="mono-label flex items-center gap-2">
            <ArrowDown size={12} style={{ color: 'var(--accent)' }} />
            {t.hero.scroll}
          </span>
          <span className="mono-label hidden sm:block">{t.hero.status}</span>
        </div>

        <div ref={marqueeRef} className="hairline-t hairline-b overflow-hidden py-4" aria-hidden>
          <div
            className={`animate-marquee flex w-max${marqueeOn ? ' is-running' : ''}`}
            style={{ '--marquee-duration': '36s' } as React.CSSProperties}
          >
            {[...marquee, ...marquee].map((item, i) => (
              <span
                key={i}
                className="display flex items-center gap-6 pe-6 whitespace-nowrap"
                style={{
                  fontSize: isAr ? '1.05rem' : '1.15rem',
                  fontWeight: 560,
                  color: i % 2 ? 'var(--faint)' : 'var(--ink)',
                }}
              >
                {item}
                <span style={{ color: 'var(--accent)', fontWeight: 400 }}>/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
